import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';

import { ApiService } from '@services/api.service';
import { environment } from '@env/environment';

import {
  CountryModel,
  ForeignPortModel,
  HtsNumberModel,
  IncoTermModel,
  LocationOfGoodsModel,
  PortDirector,
  PortModel,
  StateModel,
  UnitModel,
} from '@data-layer/models/common';
import { switchMap } from 'rxjs/operators';
import { LclPortModel } from '@data-layer/models/lcl';
import { IndexedDbService } from '@shared/indexed-db/services';
import { INDEXED_DB_CONFIG } from '@app/app-indexed-db.const';
import { OnlineRepository } from '@data-layer/repositories/online.repository';

@Injectable({
  providedIn: 'root',
})
export class CodeRepository {
  constructor(private apiService: ApiService, private iDbService: IndexedDbService, private onlineRepository: OnlineRepository) {}

  public searchCountry(query: string): Observable<CountryModel[]> {
    if (!query) {
      return of(null);
    }

    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.CUSTOMS_COUNTRIES).pipe(
      // add fallback if indexed db is empty or error occurred
      switchMap((countries: CountryModel[]) => {
        if (!!countries?.length) {
          return of(
            countries.filter(
              (item: CountryModel) =>
                item.code.toUpperCase().includes(query.toUpperCase()) || item.description.toUpperCase().includes(query.toUpperCase()),
            ),
          );
        } else {
          return this.apiService.get<CountryModel[]>(
            `${environment.url.prefixCustoms}v2/countries`,
            { params: { query } },
            { isShowLoader: false },
          );
        }
      }),
    );
  }

  public searchMarineCountry(query: string): Observable<CountryModel[]> {
    if (!query) {
      return of(null);
    }

    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.MARINE_INSURANCE_COUNTRIES).pipe(
      switchMap((countries: CountryModel[]) => {
        if (!!countries?.length) {
          return of(
            countries.filter(
              (item: CountryModel) =>
                item.code.toUpperCase().includes(query.toUpperCase()) || item.description.toUpperCase().includes(query.toUpperCase()),
            ),
          );
        } else {
          return this.apiService.get<CountryModel[]>(
            `${environment.url.prefixInsurance}v2/countries`,
            { params: { query } },
            { isShowLoader: false },
          );
        }
      }),
    );
  }

  public getCountry(country: string): Observable<CountryModel> {
    if (!country) {
      return of(null);
    }

    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.CUSTOMS_COUNTRIES).pipe(
      switchMap((countries: CountryModel[]) => {
        if (!!countries?.length) {
          return of(countries.find((item: CountryModel) => item.code.toUpperCase().includes(country.toUpperCase())));
        } else {
          return this.apiService.get<CountryModel>(`${environment.url.prefixCustoms}v2/countries/${country}`, null, {
            isShowLoader: false,
          });
        }
      }),
    );
  }

  public getIncoterms(): Observable<IncoTermModel[]> {
    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.INCOTERMS).pipe(
      switchMap((incoterms: IncoTermModel[]) => {
        if (!!incoterms?.length) {
          return of(incoterms);
        } else {
          return this.apiService.get<IncoTermModel[]>(`${environment.url.prefixCustoms}v2/incoterms`, null, { isShowLoader: false });
        }
      }),
    );
  }

  public getIncoterm(code: string): Observable<IncoTermModel> {
    if (!code) {
      return of(null);
    }

    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.INCOTERMS).pipe(
      switchMap((incoterms: IncoTermModel[]) => {
        if (!!incoterms?.length) {
          return of(incoterms.find((item: IncoTermModel) => item.code.toUpperCase().includes(code.toUpperCase())));
        } else {
          return this.apiService.get<IncoTermModel>(`${environment.url.prefixCustoms}v2/incoterms/${code}`, null, { isShowLoader: false });
        }
      }),
    );
  }

  public searchLocations(query: string = ''): Observable<LocationOfGoodsModel[]> {
    return this.apiService.get<LocationOfGoodsModel[]>(
      `${environment.url.prefixCustoms}v2/locations`,
      {
        params: { query },
      },
      { isUsed: true, isShowLoader: false },
    );
  }

  public getLocation(code: string): Observable<LocationOfGoodsModel> {
    return this.apiService.get<LocationOfGoodsModel>(`${environment.url.prefixCustoms}v2/locations/${code}`, null, { isUsed: true });
  }

  public searchHts(query: string): Observable<HtsNumberModel[]> {
    return this.apiService.get(
      `${environment.url.prefixCustoms}v2/hts/`,
      {
        params: {
          query,
        },
      },
      { isUsed: true, isShowLoader: false },
    );
  }

  public searchForeignPort(query: string): Observable<ForeignPortModel[]> {
    return this.apiService.get<ForeignPortModel[]>(
      `${environment.url.prefixCustoms}v2/foreign-ports`,
      {
        params: { query },
      },
      { isUsed: true, isShowLoader: false },
    );
  }

  public getForeignPort(id: number): Observable<ForeignPortModel> {
    return this.apiService.get<ForeignPortModel>(`${environment.url.prefixCustoms}v2/foreign-ports/${id}`, null, {
      isUsed: true,
      isShowLoader: false,
    });
  }

  public searchLineUnits(unit: string, isShowLoader: boolean = false): Observable<UnitModel[]> {
    if (!unit) {
      return of(null);
    }

    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.LINE_UNITS).pipe(
      switchMap((units: UnitModel[]) => {
        if (!!units?.length) {
          return of(
            units.filter(
              (item: UnitModel) =>
                item.code.toUpperCase().includes(unit.toUpperCase()) || item.description.toUpperCase().includes(unit.toUpperCase()),
            ),
          );
        } else {
          return this.apiService.get<UnitModel[]>(
            `${environment.url.prefixCustoms}v2/line-units`,
            { params: { query: unit } },
            { isShowLoader: false },
          );
        }
      }),
    );
  }

  public getShipmentUnits(): Observable<UnitModel[]> {
    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.SHIPMENT_UNITS).pipe(
      switchMap((units: UnitModel[]) => {
        if (!!units?.length) {
          return of(units);
        } else {
          return this.apiService.get<UnitModel[]>(`${environment.url.prefixCustoms}v2/shipment-units`, null, { isShowLoader: false });
        }
      }),
    );
  }

  public searchShipmentUnits(unit: string, isShowLoader: boolean = false): Observable<UnitModel[]> {
    if (!unit) {
      return of(null);
    }

    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.SHIPMENT_UNITS).pipe(
      switchMap((units: UnitModel[]) => {
        if (!!units?.length) {
          return of(
            units.filter(
              (item: UnitModel) =>
                item.code.toUpperCase().includes(unit.toUpperCase()) || item.description.toUpperCase().includes(unit.toUpperCase()),
            ),
          );
        } else {
          return this.apiService.get<UnitModel[]>(
            `${environment.url.prefixCustoms}v2/shipment-units`,
            { params: { query: unit } },
            { isShowLoader: false },
          );
        }
      }),
    );
  }

  public searchIsfUnits(query: string, isShowLoader: boolean = false): Observable<UnitModel[]> {
    if (!query) {
      return of(null);
    }

    return this.iDbService.toArray(INDEXED_DB_CONFIG.TABLES.ISF_UNITS).pipe(
      switchMap((units: UnitModel[]) => {
        if (!!units?.length) {
          return of(
            units.filter(
              (item: UnitModel) =>
                item.code.toUpperCase().includes(query.toUpperCase()) || item.description.toUpperCase().includes(query.toUpperCase()),
            ),
          );
        } else {
          return this.apiService.get<UnitModel[]>(
            `${environment.url.prefixCustoms}v2/isf-units`,
            { params: { query } },
            { isShowLoader: false },
          );
        }
      }),
    );
  }

  public searchDomesticPorts(query: string): Observable<PortModel[]> {
    return this.apiService.get(
      `${environment.url.prefixCustoms}v2/domestic-ports/`,
      {
        params: {
          query,
        },
      },
      { isUsed: true, isShowLoader: false },
    );
  }

  public getDomesticPort(code: string, isShowLoader: boolean = false): Observable<PortModel> {
    return this.apiService.get<PortModel>(`${environment.url.prefixCustoms}v2/domestic-ports/${code}`, null, {
      isUsed: true,
      isShowLoader,
    });
  }

  public getPortDirectorByDomesticPort(code: string, isShowLoader: boolean = false): Observable<PortDirector> {
    return this.apiService.get<PortDirector>(`${environment.url.prefixCustoms}v2/domestic-ports/${code}/port-director`, null, {
      isUsed: true,
      isShowLoader,
    });
  }

  public searchState(countryCode: string, query: string): Observable<StateModel[]> {
    return this.apiService.get<StateModel[]>(
      `${environment.url.prefixCustoms}v2/countries/${countryCode}/states`,
      {
        params: {
          query,
        },
      },
      {
        isUsed: true,
        isShowLoader: false,
      },
    );
  }

  public getState(code: string): Observable<StateModel> {
    return this.apiService.get<StateModel>(`${environment.url.prefixCustoms}v2/states/${code}`, null, {
      isUsed: true,
      isShowLoader: false,
    });
  }

  public searchFreightPort(query: string): Observable<LclPortModel[]> {
    return this.apiService.get<LclPortModel[]>(
      `${environment.url.prefixFreight}ports`,
      {
        params: { query },
      },
      { isUsed: true, isShowLoader: false },
    );
  }

  public searchFreightPortV2(query: string): Observable<LclPortModel[]> {
    return this.apiService.get<LclPortModel[]>(
      `${environment.url.prefixFreight}v2/ports`,
      {
        params: { query },
      },
      { isUsed: true, isShowLoader: false },
    );
  }
}
